import React, { useEffect } from "react";
import { Box, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { HiOutlineXMark } from "react-icons/hi2";
import { c, glassSurface } from "../../classroomColors";
import { STANDE } from "../../standeTokens";

/**
 * The room's dock: a slim glass rail on the left, and one glass panel beside
 * it for whichever of its sections is open.
 *
 * The canvas is the room, so what sits on it has to cost as little of it as
 * it can. Folded, the dock is a column of icons whose badges still say what
 * matters — how many are here, how many messages are unread. One section
 * opens at a time, so the room never grows a wall of panels, and the glass
 * lets the board show through behind whatever is open.
 *
 * Sections are data, so a page adds its own (People, Chat, Rooms) without
 * this knowing what is in them:
 *   { id, label, icon, badge?, attention?, title?, grow?, width?, render() }
 * `grow` lets a section take the full height (a conversation); otherwise the
 * panel is only as tall as what is in it.
 */
const RoomDock = ({ sections, openId, onOpen, top = "84px", left = "20px", bottom = "76px", children }) => {
  const open = sections.find((s) => s.id === openId) || null;

  // Escape folds the panel — unless someone is typing in it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      onOpen(null);
    };
    // Capture phase, so a focused control cannot keep the key from us.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onOpen]);

  return (
    <>
      <Flex
        position="absolute"
        top={top}
        left={left}
        zIndex={19}
        direction="column"
        align="center"
        gap="12px"
        pointerEvents="none"
      >
        <Flex
          as="nav"
          aria-label="Room"
          direction="column"
          gap="4px"
          p="5px"
          borderRadius="18px"
          pointerEvents="auto"
          {...glassSurface}
        >
          {sections.map((s) => {
            const isOpen = s.id === openId;
            return (
              <Tooltip key={s.id} label={s.label} placement="right" hasArrow fontSize="11px" isDisabled={isOpen}>
                <Flex
                  as="button"
                  type="button"
                  aria-label={s.label}
                  aria-expanded={isOpen}
                  onClick={() => onOpen(isOpen ? null : s.id)}
                  position="relative"
                  boxSize="40px"
                  align="center"
                  justify="center"
                  borderRadius="13px"
                  bg={isOpen ? c.solidBg : "transparent"}
                  color={isOpen ? c.solidText : c.textSub}
                  _hover={isOpen ? {} : { bg: c.surface, color: c.text }}
                  transition="background 0.15s, color 0.15s"
                >
                  <Icon as={s.icon} boxSize="19px" />
                  {s.badge != null && s.badge !== 0 && (
                    <Flex
                      position="absolute"
                      top="3px"
                      right="3px"
                      minW="16px"
                      h="16px"
                      px="4px"
                      borderRadius="200px"
                      align="center"
                      justify="center"
                      fontSize="9.5px"
                      fontWeight="600"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      // Something waiting on you is the blue; a count is ink.
                      bg={s.attention ? c.info : isOpen ? c.solidText : c.solidBg}
                      color={s.attention ? STANDE.black : isOpen ? c.solidBg : c.solidText}
                      boxShadow={`0 0 0 2px ${c.surface}`}
                    >
                      {s.badge}
                    </Flex>
                  )}
                </Flex>
              </Tooltip>
            );
          })}
        </Flex>

        {/* Anything else that belongs down the side (the question groups). */}
        <Box pointerEvents="auto">{children}</Box>
      </Flex>

      {open && (
        <Flex
          key={open.id}
          role="region"
          aria-label={open.label}
          position="absolute"
          top={top}
          left={`calc(${left} + 62px)`}
          bottom={open.grow ? bottom : undefined}
          maxH={`calc(100vh - ${top} - ${bottom})`}
          width={`min(${open.width || 320}px, calc(100vw - 110px))`}
          zIndex={19}
          direction="column"
          borderRadius="18px"
          overflow="hidden"
          {...glassSurface}
          sx={{ animation: "standeDockIn 0.18s ease-out" }}
        >
          <Flex align="center" justify="space-between" px="16px" pt="14px" pb="10px">
            <Text fontSize="14px" fontWeight="600" color={c.text}>
              {open.title || open.label}
            </Text>
            <Flex
              as="button"
              type="button"
              aria-label={`Close ${open.label}`}
              onClick={() => onOpen(null)}
              boxSize="26px"
              align="center"
              justify="center"
              borderRadius="full"
              color={c.textMuted}
              _hover={{ bg: c.surface, color: c.text }}
            >
              <Icon as={HiOutlineXMark} boxSize="15px" />
            </Flex>
          </Flex>
          {open.render()}
        </Flex>
      )}
    </>
  );
};

export default RoomDock;
